"use client";

import { useCallback, useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { SheetShell, useDismiss } from "@/components/odds-screen/live/top-menus";
import { chipClass, NumberField, quietClass } from "./chrome";

const PRESETS = [30, 50, 75, 100] as const;

const RANGE = cn(
  "h-6 min-w-0 flex-1 cursor-pointer appearance-none bg-transparent focus-visible:outline-none",
  "[&::-webkit-slider-runnable-track]:h-0.5 [&::-webkit-slider-runnable-track]:rounded-full [&::-webkit-slider-runnable-track]:bg-white/[0.12]",
  "[&::-webkit-slider-thumb]:-mt-[7px] [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-primary [&::-webkit-slider-thumb]:bg-surface-base",
  "[&::-moz-range-track]:h-0.5 [&::-moz-range-track]:rounded-full [&::-moz-range-track]:bg-white/[0.12]",
  "[&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-primary [&::-moz-range-thumb]:bg-surface-base",
  "[&:focus-visible::-webkit-slider-thumb]:shadow-[0_0_0_3px_hsl(var(--primary)/0.4)]",
);

export function BoostControl({
  value, onChange, isMobile, description, className,
}: {
  value: number;
  onChange: (boost: number) => void;
  isMobile: boolean;
  description: string;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value);
  useEffect(() => { setDraft(value); }, [value]);
  const close = useCallback(() => setOpen(false), []);
  const ref = useDismiss(open, close);
  const active = value > 0;
  const commit = (next: number) => {
    const clamped = Math.min(100, Math.max(0, Math.round(next)));
    setDraft(clamped);
    if (clamped !== value) onChange(clamped);
  };

  const trigger = (
    <button
      type="button"
      aria-haspopup="dialog"
      aria-expanded={open}
      aria-label={active ? `Boost, ${value}% applied` : "Boost"}
      onClick={() => setOpen((current) => !current)}
      className={cn(quietClass(open, isMobile), className)}
    >
      <span className="data-num">Boost{active && <> <span className="font-semibold text-primary">{value}%</span></>}</span>
    </button>
  );

  const panel = (
    <div className={cn("flex flex-col", isMobile ? "gap-3 px-4 pb-[max(1rem,env(safe-area-inset-bottom))]" : "gap-2.5 p-3")}>
      <p className="text-[12px] leading-4 text-zinc-500">{description}</p>
      <div role="group" aria-label="Boost presets" className="grid grid-cols-5 gap-1.5">
        <button type="button" aria-pressed={!active} onClick={() => commit(0)} className={chipClass(!active, isMobile)}>Off</button>
        {PRESETS.map((preset) => (
          <button key={preset} type="button" aria-pressed={value === preset} onClick={() => commit(preset)} className={cn(chipClass(value === preset, isMobile), "data-num px-0")}>
            {preset}%
          </button>
        ))}
      </div>
      <div className="flex items-center gap-3">
        <input
          type="range"
          min={0}
          max={100}
          step={1}
          value={draft}
          aria-label="Boost percentage"
          onChange={(event) => setDraft(Number(event.target.value))}
          onPointerUp={(event) => commit(Number((event.target as HTMLInputElement).value))}
          onKeyUp={(event) => commit(Number((event.target as HTMLInputElement).value))}
          onBlur={(event) => commit(Number(event.target.value))}
          className={RANGE}
        />
        <span className="flex items-center gap-1">
          <NumberField value={draft > 0 ? draft : null} placeholder="0" min={0} max={100} label="Boost percentage value" onCommit={commit} />
          <span className="text-[12px] text-zinc-500">%</span>
        </span>
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <>
        {trigger}
        <SheetShell open={open} title="Boost" onClose={close}>
          {panel}
        </SheetShell>
      </>
    );
  }

  return (
    <div ref={ref} className="relative flex-shrink-0">
      {trigger}
      {open && (
        <div
          role="dialog"
          aria-label="Boost"
          className="absolute right-0 top-[calc(100%+6px)] z-30 w-[300px] origin-top-right rounded-lg border border-surface-line bg-surface-overlay shadow-xl animate-in fade-in-0 zoom-in-95 duration-150 motion-reduce:animate-none"
        >
          {panel}
        </div>
      )}
    </div>
  );
}
